import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useLoqateFind } from '../../hooks/queryHooks';
import Spinner from '../spinner/Spinner';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';

interface ILoqateProps {
  setSelectedId: Dispatch<SetStateAction<string | null | undefined>>;
  className?: string;
  isFinalAddressLoading?: boolean;
  onAddressNotListed: () => void;
}
const Loqate = ({ setSelectedId, className, isFinalAddressLoading, onAddressNotListed }: ILoqateProps) => {
  const [loqateQuery, setLoqateSearch] = useState<{ search: string; containerId?: string }>({ search: '' });
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { data, isLoading } = useLoqateFind(loqateQuery);

  useEffect(() => {
    const ac = new AbortController();

    document.addEventListener('keydown', e => e.key === 'Escape' && setLoqateSearch({ search: '' }), {
      signal: ac.signal
    });
    document.addEventListener(
      'click',
      e => !ref.current?.contains(e.currentTarget as Node) && setLoqateSearch({ search: '' }),
      {
        signal: ac.signal
      }
    );

    return () => ac.abort();
  }, []);

  return (
    <div className={className}>
      <label htmlFor="address_search" className="flex flex-col relative">
        Address search
        <input
          type="text"
          data-hj-suppress
          id="address_search"
          required={true}
          className={`px-4 h-12 border border-gray-300 rounded-lg mt-2 
            ${
              (loqateQuery.search && data?.items.length) || isLoading
                ? 'border-b-0 rounded-b-none focus-visible:outline-0!'
                : ''
            } 
             focus:outline-2 border-gray-300
          `}
          placeholder="Start typing your address"
          value={loqateQuery.search}
          onClick={e => e.stopPropagation()}
          title="Start typing your address"
          onChange={e => setLoqateSearch({ search: DOMPurify.sanitize(e.target.value) })}
        />
        {isFinalAddressLoading && (
          <div className="w-8 h-8 absolute right-3 top-10">
            <Spinner />
          </div>
        )}
      </label>
      <div ref={ref} className="relative">
        {(data || isLoading) && (
          <ul className="sm:absolute top-0 bg-gray-100 rounded-t-none rounded-lg border-t-0 border border-gray-300 w-full max-h-36 sm:max-h-48 overflow-y-auto sm:overflow-x-hidden z-10">
            {isLoading && (
              <li className="px-4 py-2 cursor-pointer border-gray-300 flex justify-between text-sm">
                {t('Loading...')}
              </li>
            )}
            {data?.items?.map(item => (
              <li
                key={item.id}
                className="px-4 py-2 hover:bg-gray-200 cursor-pointer border-gray-300 flex justify-between text-sm"
                onClick={e => {
                  e.stopPropagation();
                  if (item.type === 'Address') {
                    setSelectedId(item.id);
                    setLoqateSearch({ search: '' });
                  } else {
                    setLoqateSearch({ search: item.text, containerId: item.id });
                  }
                }}
              >
                <div data-hj-suppress>
                  {item.text}, <span className="text-gray-500">{item.description}</span>
                </div>
                {item.type === 'Container' && <FeatherIcon icon="chevron-right" className="text-gray-300" />}
              </li>
            ))}
            <li onClick={onAddressNotListed} className="px-4 py-2 cursor-pointer text-sm underline font-medium">
              My address isn't listed
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default Loqate;
